class CustomHeroMediaBanner extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.autoplayDelay = parseInt(this.dataset.autoplaySpeed || 7000, 10);
    this.progressTimer = null;
    this.progressStart = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.stopProgress();
  }

  init() {
    this.initSwiper();
    this.initCountdowns();
  }

  initSwiper() {
    const slider = this.querySelector('.custom-hero-media-banner__swiper');
    if (!slider) return;

    const nextEl = this.querySelector('.custom-hero-media-banner__arrow--next');
    const prevEl = this.querySelector('.custom-hero-media-banner__arrow--prev');

    this.swiper = new Swiper(slider, {
      slidesPerView: 1,
      loop: false,
      autoHeight: true,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      navigation: {
        nextEl,
        prevEl,
      },
      autoplay: {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: this.dataset.pauseOnHover === 'true',
      },
      on: {
        init: () => {
          this.setActivePagination(0);
          this.startProgress();
        },
        slideChange: () => {
          this.setActivePagination(this.swiper.realIndex);
          this.startProgress();
        },
      },
    });

    this.querySelectorAll('[data-slide-to]').forEach((button) => {
      button.addEventListener('click', () => {
        const index = parseInt(button.dataset.slideTo, 10);
        if (!Number.isNaN(index)) {
          this.swiper.slideTo(index);
          this.swiper.autoplay?.start();
        }
      });
    });

    window.addEventListener('resize', () => {
      if (!this.swiper) return;
      this.setActivePagination(this.swiper.realIndex);
    });
  }

  setActivePagination(activeIndex) {
    const buttons = this.querySelectorAll('[data-slide-to]');
    const activeButton = buttons[activeIndex];

    buttons.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.classList.toggle('is-active', isActive);
      button.classList.toggle('is-complete', index < activeIndex);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.style.setProperty('--chmb-progress', index < activeIndex ? '100%' : '0%');
    });

    if (activeButton) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;

      this.style.setProperty(
        '--chmb-pagination-text',
        isMobile
          ? activeButton.dataset.mobilePaginationText || activeButton.dataset.paginationText || '#212121'
          : activeButton.dataset.paginationText || '#212121'
      );

      this.style.setProperty(
        '--chmb-pagination-bar',
        isMobile
          ? activeButton.dataset.mobilePaginationBar || activeButton.dataset.paginationBar || 'rgba(213, 137, 103, 0.6)'
          : activeButton.dataset.paginationBar || 'rgba(213, 137, 103, 0.6)'
      );

      this.style.setProperty(
        '--chmb-pagination-active',
        isMobile
          ? activeButton.dataset.mobilePaginationActive || activeButton.dataset.paginationActive || '#D58967'
          : activeButton.dataset.paginationActive || '#D58967'
      );
    }
  }

  startProgress() {
    this.stopProgress();

    const activeButton = this.querySelector('.custom-hero-media-banner__pagination-item.is-active');
    if (!activeButton) return;

    this.progressStart = performance.now();

    const tick = (now) => {
      const elapsed = now - this.progressStart;
      const progress = Math.min((elapsed / this.autoplayDelay) * 100, 100);

      activeButton.style.setProperty('--chmb-progress', `${progress}%`);

      if (progress < 100) {
        this.progressTimer = requestAnimationFrame(tick);
      }
    };

    this.progressTimer = requestAnimationFrame(tick);
  }

  stopProgress() {
    if (this.progressTimer) {
      cancelAnimationFrame(this.progressTimer);
      this.progressTimer = null;
    }
  }

  initCountdowns() {
    const countdowns = this.querySelectorAll('.custom-hero-media-banner__countdown');

    countdowns.forEach((countdown) => {
      const targetDate = countdown.dataset.countdown;
      if (!targetDate) return;

      const updateCountdown = () => {
        const now = new Date().getTime();
        const target = new Date(targetDate).getTime();
        const distance = target - now;

        if (distance <= 0) {
          countdown.innerHTML = 'Expired';
          return;
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        countdown.innerHTML = `
          <span>${days}</span><small>Days</small>
          <span>${hours}</span><small>Hrs</small>
          <span>${minutes}</span><small>Mins</small>
          <span>${seconds}</span><small>Secs</small>
        `;
      };

      updateCountdown();
      setInterval(updateCountdown, 1000);
    });
  }
}

customElements.define('custom-hero-media-banner', CustomHeroMediaBanner);